import React from 'react';
import { AdlButton, AdlButtonProps } from './AdlButton.component';

/**
 * Available social media platforms that have icons defined in adl-button-icons.scss
 */
export type SocialMediaPlatform = 'facebook' | 'google' | 'x';

type AdlSocialButtonProps = Omit<
	AdlButtonProps,
	'variant' | 'children' | 'leftIcon'
> & {
	platform: SocialMediaPlatform;
	children?: React.ReactNode;
};

/**
 * Helper function to capitalize platform name for display
 */
const capitalizePlatform = (platform: SocialMediaPlatform): string => {
	const platformMap: Record<SocialMediaPlatform, string> = {
		facebook: 'Facebook',
		google: 'Google',
		x: 'X',
	};
	return (
		platformMap[platform] ||
		platform.charAt(0).toUpperCase() + platform.slice(1)
	);
};

/**
 * AdlSocialButton is a specialized button component for social media platforms.
 * It always uses the 'tertiary' variant and automatically sets the left icon to the
 * social media platform icon based on the icons defined in adl-button-icons.scss.
 * The button text defaults to "Share on ${platform}" if not provided.
 * https://www.figma.com/design/hCmtUu4EcUkuqAiZukqDsH/Stamps-Design-System?node-id=8396-456112&m=dev
 * @example
 * ```tsx
 * <AdlSocialButton
 *   platform="facebook"
 *   onClick={handleClick}
 * />
 * // Renders: "Share on Facebook"
 * ```
 */
export const AdlSocialButton: React.FC<AdlSocialButtonProps> = props => {
	const { platform, children, className, ...rest } = props;

	// Default to "Share on ${platform}" if children not provided
	const buttonText =
		children !== undefined && children !== null
			? children === ''
				? null
				: children
			: `Share on ${capitalizePlatform(platform)}`;

	return (
		<AdlButton
			className={`adl-social-button${className ? ` ${className}` : ''}`}
			variant="tertiary"
			leftIcon={platform}
			{...rest}
		>
			{buttonText}
		</AdlButton>
	);
};
